import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import type { Response } from "../interfaces";

export interface OrderUser {
  user_id?: string;
  email?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
}

export interface OrderPrice {
  price_id?: string;
  order_price?: number;
  shipping_price?: number;
  processing_fee?: number;
  total_price?: number;
}

export interface Order {
  order_number: string | number;
  order_url?: string;
  order_hash?: string;
  name?: string; // receiver name
  email?: string; // receiver email
  user?: OrderUser; // sender info
  user_id?: string;
  order_price?: number;
  shipping_price?: number;
  processing_fee?: number;
  payment_charged?: number;
  credit_charged?: number;
  price?: OrderPrice;
  status: string;
  tracking_url?: string;
  shipped_on?: string | null;
  delivered_on?: string | null;
  street_address?: string;
  address_instructions?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;
  mobile?: string;
  accepted_on?: string | null;
  scheduled_on?: string | null;
  gift_type?: string | null;
  handwritten_note?: string | null;
  created_at?: string;
  updated_at?: string;
  order_products?: OrderProduct[];
  vendor_order_number?: string | null;
  occasion_id?: string | null;
  occasion_name?: string | null;
  sender_email?: string | null;
  note_attributes?: Array<{ name: string; value: string }>;
}

export interface OrderFilters {
  number?: number;
  sender?: string;
  receiver?: string;
  status?: string;
  page_number?: number;
  page_size?: number;
}

export interface OrderProduct {
  order_product_id?: string;
  order_number: number;
  product_id: number;
  quantity: number;
  product_price?: number;
  product_cost?: number;
  is_delete?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface AssignProductsRequest {
  products: Array<{
    product_id: number;
    quantity: number;
  }>;
  handwritten_note?: string;
}

export interface VendorDraftReview {
  draft_order_id: string;
  name: string | null;
  email: string | null;
  status: string | null;
  currency: string | null;
  subtotal_price: string | null;
  total_tax: string | null;
  total_price: string | null;
  invoice_url: string | null;
  line_items: Array<{
    title: string;
    quantity: number;
    variant_id: string | null;
    price: string | null;
    sku: string | null;
    discount_title?: string | null;
    discount_value?: string | null;
  }>;
  shipping_address: {
    name: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    province: string | null;
    zip: string | null;
    country: string | null;
    phone: string | null;
  } | null;
  note_attributes: Array<{ name: string; value: string }>;
  tags: string | null;
  applied_discount?: {
    title: string | null;
    description: string | null;
    value_type: string | null;
    value: string | null;
  } | null;
}

export interface AssignProductsResponse {
  message: string;
  order_number: number;
  order_products?: OrderProduct[];
  products?: OrderProduct[];
  vendor_invoice_url?: string | null;
  vendor_order_number?: string | null;
  vendor_draft_order_id?: string | null;
  vendor_draft?: VendorDraftReview | null;
  finalized?: boolean;
}

export const orderApi = createApi({
  reducerPath: "orderApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Order"],
  endpoints: (builder) => ({
    getOrders: builder.query<Response<Order[]>, OrderFilters | void>({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters) {
          if (filters.number) params.append('number', filters.number.toString());
          if (filters.sender) params.append('sender', filters.sender);
          if (filters.receiver) params.append('receiver', filters.receiver);
          if (filters.status) params.append('status', filters.status);
          if (filters.page_number !== undefined) params.append('page_number', filters.page_number.toString());
          if (filters.page_size !== undefined) params.append('page_size', filters.page_size.toString());
        }
        const queryString = params.toString();
        return {
          url: `order/fetchAllOrders${queryString ? `?${queryString}` : ''}`,
          method: "GET",
        };
      },
      providesTags: ["Order"],
    }),
    updateOrder: builder.mutation<
      Response<Order>,
      { id: string; data: Partial<Order> }
    >({
      query: ({ id, data }) => ({
        url: `order/updateAdminOrder/${id}`,
        method: "PUT",
        data,
      }),
      invalidatesTags: ["Order"],
    }),
    updateOrderHandwrittenNote: builder.mutation<
      { message?: string },
      { id: string; handwritten_note: string }
    >({
      query: ({ id, handwritten_note }) => ({
        url: `order/updateAdminOrder/${id}`,
        method: "PUT",
        data: { handwritten_note },
      }),
    }),
    syncOrderToZapier: builder.mutation<Response<unknown>, { orderNumber: string | number }>({
      query: ({ orderNumber }) => ({
        url: `integration/zapier/order/${orderNumber}/capture-data`,
        method: "POST",
      }),
    }),
    assignProductsToOrder: builder.mutation<
      AssignProductsResponse,
      {
        order_number: number;
        products: Array<{ product_id: number; quantity: number }>;
        vendor_order_number?: string;
        finalize?: boolean;
        handwritten_note?: string;
      }
    >({
      query: ({ order_number, products, vendor_order_number, finalize, handwritten_note }) => ({
        url: `order/${order_number}/assign-products`,
        method: "POST",
        data: {
          products,
          ...(vendor_order_number ? { vendor_order_number } : {}),
          ...(finalize ? { finalize: true } : {}),
          ...(handwritten_note !== undefined ? { handwritten_note } : {}),
        },
      }),
      // Refresh lists only when assignment is finalized (products saved / status changed).
      invalidatesTags: (result) => {
        // SuccessResponseInterceptor wraps payload under `data`.
        const payload =
          result && typeof result === "object" && "data" in result
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ((result as any).data ?? result)
            : result;
        if (payload?.finalized === false || payload?.vendor_invoice_url) {
          return [];
        }
        return ["Order"];
      },
    }),
    getOrderProducts: builder.query<Response<OrderProduct[]>, number | string>({
      query: (order_number) => ({
        url: `order/${order_number}/assign-products`,
        method: "GET",
      }),
      providesTags: ["Order"],
    }),
    deleteOrder: builder.mutation<{ message?: string }, { orderHash: string }>({
      query: ({ orderHash }) => ({
        url: `order/${orderHash}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Order"],
    }),    // Separate export endpoint - should return all records without pagination
    exportOrders: builder.query<Response<Order[]>, Omit<OrderFilters, 'page_number' | 'page_size'> | void>({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters) {
          if (filters.number) params.append('number', filters.number.toString());
          if (filters.sender) params.append('sender', filters.sender);
          if (filters.receiver) params.append('receiver', filters.receiver);
          if (filters.status) params.append('status', filters.status);
        }
        const queryString = params.toString();
        return {
          url: `order/exportOrders${queryString ? `?${queryString}` : ''}`,
          method: "GET",
        };
      },
      keepUnusedDataFor: 0,
    }),

  }),
});

export const {
  useGetOrdersQuery,
  useUpdateOrderMutation,
  useUpdateOrderHandwrittenNoteMutation,
  useSyncOrderToZapierMutation,
  useAssignProductsToOrderMutation,
  useGetOrderProductsQuery,
  useDeleteOrderMutation,
  useLazyExportOrdersQuery,
} = orderApi;

